'use client';

/**
 * Public table reservation request.
 *
 * Reworked from a 448px stack of five bare inputs. The changes that matter to a
 * guest standing outside a restaurant with one hand free:
 *   · party size is a row of tappable chips, not a number spinner , the common
 *     answer (1–6) is now one tap instead of a keyboard;
 *   · the date field is bounded by the venue's own lead time, so a guest cannot
 *     submit a request that will be rejected for being too soon;
 *   · after submitting, the status is a progress track , requested → confirmed ,
 *     rather than a badge that gives no sense of where things stand;
 *   · every terminal state offers a next step, so nothing is a dead end.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { CalendarClock, Check, CheckCircle2, Clock, Users, X } from 'lucide-react';

import { useLoadingState } from '@/hooks/useLoadingState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/components/ui/utils';
import {
  CustomerAccountShortcut,
  CustomerSection,
  CustomerSurface,
  CustomerSurfaceError,
  CustomerSurfaceHeader,
  CustomerSurfaceLoading,
  CustomerSurfaceSplit,
  CustomerSurfaceStatus,
  useCustomerSurfaceTheme,
} from '@/components/customer-surface/CustomerSurface';
import { ChoiceChip, CustomerField } from '@/components/customer-surface/CustomerForm';
import { venueBackTarget } from '@/lib/customer/order-context';
import { legacyMenuPath } from '@/lib/routing/place-href';
import {
  getReservationForSlug,
  removeGuestReservation,
  saveGuestReservation,
} from '@/lib/customer/guest-reservations';
import { getGuestUsername } from '@/lib/customer/guest-identity';
import type { BranchOperatingHours } from '@/lib/branch-operating-hours';

type ReserveContext = {
  enabled: boolean;
  minLeadMinutes: number;
  operatingHours?: BranchOperatingHours;
  businessName: string | null;
  branchName: string | null;
  businessSlug: string | null;
  branchSlug: string | null;
  logoUrl: string | null;
  themeId: string | null;
};

type RequestStatus = 'pending' | 'waitlisted' | 'confirmed' | 'declined' | 'cancelled';

const STATUS_COPY: Record<
  RequestStatus,
  { label: string; note: string; tone: 'pending' | 'success' | 'error' | 'info' }
> = {
  pending: {
    label: 'Request sent',
    note: 'The venue is looking at it now. This page updates on its own , you can leave it open.',
    tone: 'pending',
  },
  waitlisted: {
    label: 'You’re on the waitlist',
    note: 'The venue is full at that time. We’ll update this page the moment a table frees up.',
    tone: 'info',
  },
  confirmed: {
    label: 'Table confirmed',
    note: 'You’re booked. See you then!',
    tone: 'success',
  },
  declined: {
    label: 'That time isn’t available',
    note: 'The venue couldn’t fit this booking in. Try a different time , there’s often room either side of it.',
    tone: 'error',
  },
  cancelled: {
    label: 'Request cancelled',
    note: 'This request was cancelled. You can make a new one whenever you like.',
    tone: 'info',
  },
};

const QUICK_PARTY_SIZES = ['1', '2', '3', '4', '5', '6', '7'];
const RESERVATION_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toLocalInputValue(date: Date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function reservationSlotsFor(
  date: Date,
  hours: BranchOperatingHours | undefined,
  leadMinutes: number,
): Date[] {
  const weekday = RESERVATION_DAYS[date.getDay()];
  const day = hours?.[weekday];
  if (day?.closed) return [];
  const shifts = day?.shifts?.length
    ? day.shifts
    : day?.open && day.close
      ? [{ open: day.open, close: day.close }]
      : [{ open: '12:00', close: '21:30' }];
  const earliest = Date.now() + Math.max(leadMinutes, 30) * 60_000;
  const available: Date[] = [];

  for (const shift of shifts) {
    const [openHour, openMinute] = shift.open.split(':').map(Number);
    let [closeHour, closeMinute] = shift.close.split(':').map(Number);
    if (closeHour * 60 + closeMinute <= openHour * 60 + openMinute) closeHour += 24;
    for (let minute = openHour * 60 + openMinute; minute <= closeHour * 60 + closeMinute - 60; minute += 30) {
      const slot = new Date(date);
      slot.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
      if (slot.getTime() >= earliest) available.push(slot);
    }
  }

  return available;
}

export default function ReservePageClient({ chow = false, experience = 'v1' }: { chow?: boolean; experience?: 'v1' | 'v2' }) {
  const params = useParams<{ slug: string }>();
  const router = useRouter();
  const slug = params?.slug;
  // Doc 71 MNRQ-UX-01: "back"/"close" on a reserve surface returns to the
  // previous meaningful Menuraq task — the guest reads the menu inline and
  // "back to the menu" must LAND on that same venue's in-app menu (this is a
  // public customer surface; Chow is forbidden for it, see §5/§4.1). The venue
  // target is resolved from DATA carved off the reserve wire (`businessSlug` /
  // `branchSlug`, projected in app/api/public/reservations/[slug]/route.ts from
  // the same rows already in scope), never reconstructed from the URL, never
  // routed through Chow. Falls back to the booking slug only while the wire is
  // still loading / on a data error, so a guest is never stranded on a blank
  // page or a marketing home.
  const [ctx, setCtx] = useState<ReserveContext | null>(null);

  const backToMenu = useCallback(
    (targetSlug: string) => {
      const target = venueBackTarget({
        businessSlug: ctx?.businessSlug ?? null,
        branchSlug: ctx?.branchSlug ?? null,
        experience,
      });
      router.push(target ?? legacyMenuPath(targetSlug, experience === 'v2' ? { experience: 'v2' } : undefined));
    },
    [ctx?.businessSlug, ctx?.branchSlug, experience, router],
  );
  const { isLoading: loading, setIsLoading: setLoading } = useLoadingState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [partySize, setPartySize] = useState('2');
  const [customParty, setCustomParty] = useState(false);
  const [when, setWhen] = useState('');
  const [dayOffset, setDayOffset] = useState(0);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [requestId, setRequestId] = useState<string | null>(null);
  const [status, setStatus] = useState<RequestStatus | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadContext = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`/api/public/reservations/${slug}`, { cache: 'no-store' });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Could not load this page.');
      setCtx(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load this page.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const pollStatus = useCallback((id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const res = await fetch(`/api/public/reservations/request/${id}`, { cache: 'no-store' });
        const data = await res.json();
        if (data.ok) {
          setStatus(data.request.status);
          if (['confirmed', 'declined', 'cancelled'].includes(data.request.status) && pollRef.current) {
            clearInterval(pollRef.current);
          }
        }
      } catch {
        /* keep polling */
      }
    };
    void tick(); // resolve immediately (e.g. when restoring on refresh), then poll
    pollRef.current = setInterval(tick, 5000);
  }, []);

  // Restore a previously-submitted request for this location so a refresh or
  // return visit still shows its live status , no account needed.
  useEffect(() => {
    if (!slug || !ctx || requestId) return;
    const stored = getReservationForSlug(slug);
    if (!stored) return;
    setRequestId(stored.id);
    setWhen(stored.whenISO);
    setPartySize(String(stored.partySize));
    setStatus('pending');
    pollStatus(stored.id);
  }, [slug, ctx, requestId, pollStatus]);

  const submit = useCallback(async () => {
    if (!slug) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/reservations/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partySize: Number(partySize),
          requestedAt: when ? new Date(when).toISOString() : '',
          guestName: name,
          guestContact: contact,
          notes,
          guestUsername: getGuestUsername() ?? undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Could not submit your request.');
      setRequestId(data.requestId);
      setStatus(data.status);
      saveGuestReservation({
        id: data.requestId,
        slug,
        whenISO: when ? new Date(when).toISOString() : new Date().toISOString(),
        partySize: Number(partySize),
        businessName: ctx?.businessName ?? null,
        branchName: ctx?.branchName ?? null,
        createdAt: Date.now(),
      });
      pollStatus(data.requestId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit your request.');
    } finally {
      setSubmitting(false);
    }
  }, [slug, partySize, when, name, contact, notes, pollStatus, ctx]);

  const cancel = useCallback(async () => {
    if (!requestId) return;
    setCancelling(true);
    try {
      const res = await fetch(`/api/public/reservations/request/${requestId}`, { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        setStatus('cancelled');
        removeGuestReservation(requestId);
      } else {
        setError(data.error || 'Could not cancel this request.');
      }
    } catch {
      setError('Could not cancel this request. Check your connection and try again.');
    } finally {
      setCancelling(false);
    }
  }, [requestId]);

  // Return to a blank form (after a resolved request) without losing the page.
  const startOver = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (requestId) removeGuestReservation(requestId);
    setRequestId(null);
    setStatus(null);
    setName('');
    setContact('');
    setNotes('');
    setError(null);
    setWhen('');
    setDayOffset(0);
  }, [requestId, ctx]);

  const brand = ctx
    ? {
        businessName: ctx.businessName,
        branchName: ctx.branchName,
        logoUrl: ctx.logoUrl,
        themeId: ctx.themeId,
      }
    : null;
  const { headingStyle, title: brandTitle } = useCustomerSurfaceTheme(brand);

  const days = useMemo(() => Array.from({ length: 21 }).map((_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + index);
    return date;
  }), []);
  const selectedDate = days[dayOffset] ?? days[0];
  const availableTimes = useMemo(
    () => selectedDate
      ? reservationSlotsFor(selectedDate, ctx?.operatingHours, ctx?.minLeadMinutes ?? 30)
      : [],
    [selectedDate, ctx?.operatingHours, ctx?.minLeadMinutes],
  );

  if (loading) {
    return <CustomerSurfaceLoading width="split" label="Loading reservations" rows={2} />;
  }

  if (loadError && !ctx) {
    return (
      <CustomerSurfaceError
        title="We couldn’t open reservations"
        message={loadError}
        onRetry={() => void loadContext()}
        secondaryAction={
          slug ? (
            <Button variant="outline" className="min-h-11" onClick={() => backToMenu(slug)}>
              View the menu
            </Button>
          ) : undefined
        }
      />
    );
  }

  const venueName = brandTitle || 'this venue';

  if (ctx && !ctx.enabled) {
    return (
      <CustomerSurface brand={brand} className={chow ? 'chow-reserve' : undefined}>
        <CustomerSurfaceHeader className={chow ? 'chow-reserve-header' : undefined} brand={brand} onBack={slug ? () => backToMenu(slug) : undefined} eyebrow="Reservations" title={brandTitle || 'Reservations'} trailing={<CustomerAccountShortcut businessSlug={ctx.businessSlug} branchSlug={ctx.branchSlug} experience={experience} />} />
        <div className="mx-auto w-full max-w-xl px-4 py-12 sm:px-6">
          <div className="flex flex-col items-center rounded-[var(--radius-lg)] border border-[var(--cs-border)] bg-card px-6 py-14 text-center">
            <span className="flex size-14 items-center justify-center rounded-full bg-[var(--color-bg-sunken)] text-[var(--color-text-secondary)]">
              <CalendarClock className="size-7" aria-hidden="true" />
            </span>
            <h1 className="mt-5 text-lg font-semibold" style={headingStyle}>
              Not taking online reservations
            </h1>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-[var(--color-text-secondary)]">
              {venueName} handles bookings another way right now , the menu page has their phone number and directions.
            </p>
            {slug ? (
              <Button className="mt-6 min-h-11" onClick={() => backToMenu(slug)}>
                Go to the menu
              </Button>
            ) : null}
          </div>
        </div>
      </CustomerSurface>
    );
  }

  // ── Submitted → live status ───────────────────────────────────────────────

  if (requestId && status) {
    const copy = STATUS_COPY[status];
    const resolved = status === 'confirmed' || status === 'declined' || status === 'cancelled';
    const good = status === 'confirmed';
    const bad = status === 'declined' || status === 'cancelled';

    return (
      <CustomerSurface brand={brand} className={chow ? 'chow-reserve' : undefined}>
        <CustomerSurfaceHeader className={chow ? 'chow-reserve-header' : undefined} brand={brand} onBack={slug ? () => backToMenu(slug) : undefined} eyebrow="Your booking" title={brandTitle || 'Reservation'} trailing={<CustomerAccountShortcut businessSlug={ctx?.businessSlug} branchSlug={ctx?.branchSlug} experience={experience} />} />
        <div className="mx-auto w-full max-w-xl px-4 py-8 sm:px-6 sm:py-12">
          <div className="flex flex-col items-center text-center">
            <span
              className={cn(
                'flex size-16 items-center justify-center rounded-full',
                good
                  ? 'bg-[var(--color-success)]/12 text-[var(--color-success)] text-[var(--color-success)]'
                  : bad
                    ? 'bg-[var(--color-bg-sunken)] text-[var(--color-text-secondary)]'
                    : 'bg-primary/10 text-primary',
              )}
            >
              {good ? (
                <CheckCircle2 className="size-8" aria-hidden="true" />
              ) : bad ? (
                <X className="size-8" aria-hidden="true" />
              ) : (
                <Clock className="size-8" aria-hidden="true" />
              )}
            </span>
            <h1 className="mt-5 text-2xl font-semibold tracking-tight" style={headingStyle}>
              {copy.label}
            </h1>
            <p className="mt-2 max-w-sm text-base leading-relaxed text-[var(--color-text-secondary)]">{copy.note}</p>
          </div>

          {/* Where the request stands, at a glance. A badge told the guest the
              state but not the shape of the journey; this shows both. */}
          {!bad ? (
            <ol className="mt-8 flex items-center gap-2" aria-label="Booking progress">
              {[
                { key: 'sent', label: 'Sent', done: true },
                { key: 'reviewing', label: 'With the venue', done: status !== 'pending' },
                { key: 'confirmed', label: 'Confirmed', done: status === 'confirmed' },
              ].map((step) => (
                <li key={step.key} className="flex flex-1 items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'h-1.5 rounded-full transition-colors',
                        step.done ? 'bg-primary' : 'bg-[var(--color-bg-sunken)]',
                      )}
                    />
                    <p
                      className={cn(
                        'mt-2 truncate text-2xs font-medium',
                        step.done ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]',
                      )}
                    >
                      {step.label}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          ) : null}

          <Card className={cn('mt-8', chow && 'chow-reserve-card')}>
            <CardContent className="divide-y divide-border p-0">
              <div className="flex items-center justify-between gap-4 px-5 py-4">
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <CalendarClock className="size-4" aria-hidden="true" />
                  When
                </span>
                <span className="text-right text-base font-semibold">
                  {when
                    ? new Date(when).toLocaleString(undefined, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 px-5 py-4">
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <Users className="size-4" aria-hidden="true" />
                  Party
                </span>
                <span className="text-base font-semibold">
                  {partySize} {Number(partySize) === 1 ? 'guest' : 'guests'}
                </span>
              </div>
            </CardContent>
          </Card>

          {error ? <CustomerSurfaceStatus tone="error" className="mt-4">{error}</CustomerSurfaceStatus> : null}

          <div className="mt-6 space-y-2">
            {status === 'pending' || status === 'waitlisted' ? (
              <Button
                variant="outline"
                className="min-h-12 w-full"
                disabled={cancelling}
                onClick={() => void cancel()}
              >
                {cancelling ? 'Cancelling…' : 'Cancel this request'}
              </Button>
            ) : null}
            {resolved ? (
              <Button className="min-h-12 w-full" onClick={startOver}>
                {status === 'confirmed' ? 'Book another table' : 'Try a different time'}
              </Button>
            ) : null}
            {slug ? (
              <Button variant="ghost" className="min-h-11 w-full" onClick={() => backToMenu(slug)}>
                Browse the menu while you wait
              </Button>
            ) : null}
          </div>

          <p className="mt-6 text-center text-xs leading-relaxed text-[var(--color-text-secondary)]">
            Bookmark{' '}
            <a
              className="font-medium text-[var(--color-text-primary)] underline underline-offset-2"
              href={`/reserve/request/${requestId}${experience === 'v2' ? '?experience=v2' : ''}`}
            >
              your status page
            </a>{' '}
            to check back from any device.
          </p>
        </div>
      </CustomerSurface>
    );
  }

  // ── Request form ──────────────────────────────────────────────────────────

  const parsedPartySize = Number(partySize);
  const canSubmit = Boolean(
    when &&
    name.trim() &&
    contact.trim() &&
    Number.isInteger(parsedPartySize) &&
    parsedPartySize >= 1 &&
    parsedPartySize <= 200,
  ) && !submitting;

  const asideCard = (
    <Card className={chow ? 'chow-reserve-card' : undefined}>
      <CardContent className="space-y-4 p-5">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-[var(--color-text-secondary)]">Your request</p>
          <p className="mt-1.5 text-xl font-semibold leading-snug" style={headingStyle}>
            {when
              ? new Date(when).toLocaleString(undefined, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Pick a time'}
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {partySize} {Number(partySize) === 1 ? 'guest' : 'guests'} at {venueName}
          </p>
        </div>

        <ul className="space-y-2.5 border-t border-[var(--cs-border)] pt-4 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          <li className="flex gap-2.5">
            <Check className="mt-0.5 size-4 shrink-0 text-[var(--color-success)] text-[var(--color-success)]" aria-hidden="true" />
            No account needed , we only use your contact to confirm.
          </li>
          <li className="flex gap-2.5">
            <Check className="mt-0.5 size-4 shrink-0 text-[var(--color-success)] text-[var(--color-success)]" aria-hidden="true" />
            The venue confirms; this page updates by itself.
          </li>
          <li className="flex gap-2.5">
            <Check className="mt-0.5 size-4 shrink-0 text-[var(--color-success)] text-[var(--color-success)]" aria-hidden="true" />
            Cancel any time with one tap.
          </li>
        </ul>

        {ctx?.minLeadMinutes ? (
          <p className="border-t border-[var(--cs-border)] pt-4 text-xs leading-relaxed text-[var(--color-text-secondary)]">
            This venue needs at least {ctx.minLeadMinutes} minutes’ notice.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );

  return (
    <CustomerSurface brand={brand} className={chow ? 'chow-reserve' : undefined}>
      <CustomerSurfaceHeader
        className={chow ? 'chow-reserve-header' : undefined}
        brand={brand}
        onBack={slug ? () => backToMenu(slug) : undefined}
        eyebrow="Reservations"
        title={brandTitle || 'Reserve a table'}
        trailing={<CustomerAccountShortcut businessSlug={ctx?.businessSlug} branchSlug={ctx?.branchSlug} experience={experience} />}
      />

      <CustomerSurfaceSplit className={chow ? 'chow-reserve-split' : undefined} aside={asideCard}>
        <CustomerSection
          title="Reserve a table"
          description="Tell us when and how many , the venue confirms from here."
        >
          <Card className={chow ? 'chow-reserve-card' : undefined}>
            <CardContent className="space-y-5 p-5">
              {chow ? (
                <div className="space-y-5">
                  <fieldset className="space-y-3">
                    <legend className="text-sm font-medium">When</legend>
                    <div className="chow-reserve-days" role="group" aria-label="Choose a date">
                      {days.map((date, index) => {
                        const selected = dayOffset === index;
                        return (
                          <button
                            key={date.toISOString()}
                            type="button"
                            aria-pressed={selected}
                            aria-label={date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                            className={cn('chow-reserve-day', selected && 'is-selected')}
                            onClick={() => {
                              setDayOffset(index);
                              setWhen('');
                            }}
                          >
                            <span>{index === 0 ? 'Today' : date.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                            <strong>{date.getDate()}</strong>
                            <span>{date.toLocaleDateString(undefined, { month: 'short' })}</span>
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <fieldset className="space-y-3">
                    <legend className="text-sm font-medium">Preferred time</legend>
                    {availableTimes.length ? (
                      <div className="chow-reserve-times" role="group" aria-label="Preferred time choices">
                        {availableTimes.map((slot) => {
                          const value = toLocalInputValue(slot);
                          const selected = when === value;
                          return (
                            <button
                              key={slot.toISOString()}
                              type="button"
                              aria-pressed={selected}
                              aria-label={slot.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                              className={cn('chow-reserve-time', selected && 'is-selected')}
                              onClick={() => setWhen(value)}
                            >
                              {slot.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).replace(' ', '')}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="chow-reserve-no-times" role="status">
                        No times left on this day. Pick another day.
                      </p>
                    )}
                  </fieldset>
                </div>
              ) : (
                <CustomerField
                  label="Date & time"
                  type="datetime-local"
                  value={when}
                  min={toLocalInputValue(new Date(Date.now() + Math.max(ctx?.minLeadMinutes ?? 30, 0) * 60_000))}
                  onChange={(e) => setWhen(e.target.value)}
                />
              )}

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">How many of you?</legend>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PARTY_SIZES.map((size) => (
                    <ChoiceChip
                      key={size}
                      name="party-size"
                      value={size}
                      checked={!customParty && partySize === size}
                      onSelect={(value) => {
                        setCustomParty(false);
                        setPartySize(value);
                      }}
                    >
                      {size}
                    </ChoiceChip>
                  ))}
                  <ChoiceChip
                    name="party-size"
                    value="custom"
                    checked={customParty}
                    onSelect={() => {
                      setCustomParty(true);
                      setPartySize('8');
                    }}
                  >
                    8+
                  </ChoiceChip>
                </div>
                {customParty ? (
                  <CustomerField
                    label="Exact number of guests"
                    type="number"
                    min={8}
                    max={200}
                    value={partySize}
                    onChange={(e) => setPartySize(e.target.value)}
                  />
                ) : null}
              </fieldset>

              <CustomerField
                label="Your name"
                value={name}
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
              />

              <CustomerField
                label="Phone or email"
                value={contact}
                autoComplete="tel"
                onChange={(e) => setContact(e.target.value)}
                placeholder="024 000 0000"
                hint="Used once, to confirm your table."
              />

              <CustomerField label="Anything we should know?" optional>
                {({ id }) => (
                  <Textarea
                    id={id}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Occasion, seating preference, accessibility…"
                    className="text-base"
                  />
                )}
              </CustomerField>

              {error ? <CustomerSurfaceStatus tone="error">{error}</CustomerSurfaceStatus> : null}

              <Button
                className={cn('min-h-12 w-full text-base', chow && 'hidden md:flex')}
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {submitting ? 'Sending your request…' : 'Request this table'}
              </Button>

              <p className="text-center text-xs leading-relaxed text-[var(--color-text-secondary)]">
                This is a request, not a guarantee , you’ll see it confirmed here within a few minutes.
              </p>
            </CardContent>
          </Card>
        </CustomerSection>
      </CustomerSurfaceSplit>

      {chow ? (
        <div className="chow-reserve-footer">
          {when ? (
            <p className="chow-reserve-footer-summary">
              {new Date(when).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
              {' · '}
              {new Date(when).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              {' · '}
              {partySize} {Number(partySize) === 1 ? 'guest' : 'guests'}
            </p>
          ) : null}
          <Button className="min-h-[52px] w-full rounded-full text-base font-bold" disabled={!canSubmit} onClick={() => void submit()}>
            {submitting ? 'Sending your request…' : 'Request this table'}
          </Button>
        </div>
      ) : null}
    </CustomerSurface>
  );
}
